





























import { Component, Prop, Vue } from 'vue-property-decorator';
import { UserActionLogInfo } from '@/models/users/user-logging/user-action-log-info';
import { UserActionType } from '@/models/users/user-logging/user-action-type';
import { UserActionEntityType } from '@/models/users/user-logging/user-action-entity-type';
import { UserActionScopeType } from '@/models/users/user-logging/user-action-scope-type';
import { Reference } from '@/models/references/reference';
import { ReferenceImage, ReferenceImageSize } from '@/models/references/reference-image';
import { referencesService, notificationsService, usersService } from '@/services';
import Swal from 'sweetalert2';
import { mdbModal, mdbModalHeader, mdbModalTitle, mdbModalBody, mdbModalFooter, mdbBtn, mdbIcon } from '@folky-tools/mdbvue';

@Component({
    components: {
        mdbModal,
        mdbModalHeader,
        mdbModalTitle,
        mdbModalBody,
        mdbModalFooter,
        mdbBtn,
        mdbIcon,
        FormModal: () => import('@/components/shared/FormModal.vue'),
        ReferenceImages: () => import('@/components/references/ReferenceImages.vue'),
        FileUpload: () => import('@/components/shared/FileUpload.vue')
    }
})
export default class EditServiceImages extends Vue {
    @Prop(Reference) private service!: Reference;
    @Prop(Reference) private serviceCopy!: Reference;
    @Prop({ type: Boolean, default: false }) private show?: boolean;
    private showEditImage: boolean = false;
    private selectedImage: ReferenceImage = new ReferenceImage();
    private savingServiceImage: boolean = false;
    private imageDeletionConfirmed: boolean = false;

    private addNewImage(): void {
        this.selectedImage = new ReferenceImage({ size: ReferenceImageSize.Original });
        this.showEditImage = true;
    }

    private async deleteImage(id: number): Promise<void> {
        const questionResult = await Swal.fire({
            title: this.$i18n.t('components.services.editImagesModal.removeImage.title').toString(),
            text: this.$i18n.t('components.services.editImagesModal.removeImage.text').toString(),
            type: 'warning',
            showCancelButton: true,
            reverseButtons: true,
            focusConfirm: false,
            focusCancel: true,
            confirmButtonText: this.$i18n.t('components.services.editImagesModal.removeImage.doConfirm').toString(),
            cancelButtonText: this.$i18n.t('components.services.editImagesModal.removeImage.doCancel').toString(),
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-danger'
            },
            showLoaderOnConfirm: true,
            preConfirm: () => {
                return this.confirmImageDeleted(id);
            },
            allowOutsideClick: false,
            allowEscapeKey: false,
            animation: false
        });

        if (questionResult.value) {
            if (this.imageDeletionConfirmed) {
                notificationsService.showToastSuccessNotification('components.services.editImagesModal.notifications.deleteImageSuccess');
                this.imageDeletionConfirmed = false;
            } else {
                notificationsService.showToastErrorNotification();
            }
        }
    }

    private async confirmImageDeleted(id: number): Promise<void> {
        if (await referencesService.deleteReferenceImage(this.service.id, id)) {
            this.imageDeletionConfirmed = true;
            this.service.images = this.service.images.filter(image => image.id !== id);
            if (this.service.images.length > 0) {
                this.service.images
                    .filter(image => image.id === this.service.images[0].id)
                    .forEach(image => {
                        image.isPrincipal = true;
                    });
            }

            const currentImageCount = this.service.images.filter(image => image.size === ReferenceImageSize.Original).length;
            this.$emit('updateSelectedServiceImageCount', currentImageCount);

            await usersService.logUserAction(
                new UserActionLogInfo({
                    type: UserActionType.HasChanged,
                    entity: UserActionEntityType.Services,
                    scope: UserActionScopeType.Images,
                    entityId: this.service.id
                })
            );

            return;
        }
    }

    private changeImage(id: number): void {
        this.selectedImage = this.service.images.find(image => image.id === id && image.size === ReferenceImageSize.Original)!.clone();
        this.showEditImage = true;
    }

    private async saveImage(): Promise<void> {
        this.savingServiceImage = true;

        try {
            if (this.selectedImage.url) {
                if (this.selectedImage.id === 0 || !this.serviceCopy.images.length || this.selectedImage.url !== this.serviceCopy.images.find(image => image.id === this.selectedImage.id && image.size === this.selectedImage.size)!.url) {
                    const imageFile: File = (this.$refs.editImage as any).getFinalImageFile();

                    if (imageFile) {
                        const result = this.selectedImage.id > 0 ? await referencesService.updateReferenceImage(this.service.id, this.selectedImage.id, imageFile) : await referencesService.newReferenceImage(this.service.id, imageFile);

                        if (result) {
                            result.forEach(newImage => {
                                if (this.selectedImage.id > 0) {
                                    const image = this.service.images.find(image => image.id === newImage.id && image.size === newImage.size)!;
                                    const imageCopy = this.serviceCopy.images.find(image => image.id === newImage.id && image.size === newImage.size)!;
                                    image.url = newImage.url;
                                    imageCopy.url = newImage.url;
                                } else {
                                    this.service.images.push(newImage);
                                    this.serviceCopy.images.push(newImage);
                                }
                            });

                            const currentImageCount = this.service.images.filter(image => image.size === ReferenceImageSize.Original).length;
                            this.$emit('updateSelectedServiceImageCount', currentImageCount);
                            this.showEditImage = false;

                            notificationsService.showToastSuccessNotification('components.services.editImagesModal.notifications.saveImageSuccess');

                            await usersService.logUserAction(
                                new UserActionLogInfo({
                                    type: UserActionType.HasChanged,
                                    entity: UserActionEntityType.Services,
                                    scope: UserActionScopeType.Images,
                                    entityId: this.service.id
                                })
                            );

                            return;
                        }
                    }

                    notificationsService.showToastErrorNotification();
                    return;
                }
            }
            this.showEditImage = false;
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.savingServiceImage = false;
        }
    }

    private closeEditImage(): void {
        this.showEditImage = false;
    }

    private async closeEditImagesModal(): Promise<void> {
        this.$emit('closeEditImagesModal');
    }
}
